<template>
  <v-container fluid class="px-sm-10">
    <v-row>
      <v-col cols="auto" class="headline">Overview</v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-form ref="form" @submit.prevent="submit" v-model="valid">
          <v-card class="mb-12">
            <v-card-title>Shop Information</v-card-title>
            <v-card-text>
              <v-text-field name="shopname" label="Title" v-model="ownOverview.name" :rules="stringValidation" :disabled="isSaving"/>
              <v-text-field name="orgname" label="Organisation Name" v-model="ownOverview.org_name" :disabled="isSaving"/>
              <v-text-field name="orgnumber" label="Organisation Number" type="tel" v-model="ownOverview.org_number" :disabled="isSaving"/>
              <v-text-field name="vatnumber" label="VAT Registration Number" type="tel" v-model="ownOverview.vat_number" :disabled="isSaving"/>
              <v-textarea auto-grow rows="1" name="contact" label="Contact" type="tel" v-model="ownOverview.contact" :disabled="isSaving"/>
              <image-input
                label="Image"
                v-model="image"
                :crop-width="450"
                :crop-height="450"
                :crop-clamp="true"
                :quality="0.8"
                ouput-type="jpeg"
                class="mt-0"
                :disabled="isSaving"
              ></image-input>
            </v-card-text>

            <v-card-title>Messages</v-card-title>
            <v-card-text>
              <v-textarea
                auto-grow
                rows="1"
                name="startdialog"
                label="Open message (Shown in a dialog when opening the shop)"
                v-model="ownOverview.message_startup"
                :disabled="isSaving"
              />
              <v-textarea
                auto-grow
                rows="1"
                name="message_order_received"
                label="Message for 'Order received'"
                v-model="ownOverview.message_order_received"
                :disabled="isSaving"
                messages="Available to insert: amount, dateCreated, datePaid, operatorName, deliveryTime"
                class="mb-8"
              />
              <v-textarea
                auto-grow
                rows="1"
                name="message_order_pickup"
                label="Message for 'Order ready to pick up'"
                v-model="ownOverview.message_order_pickup"
                placeholder="Order klar, avhämtas vid #{deviceName}"
                :disabled="isSaving"
                messages="Available to insert: deviceName, amount, dateCreated, datePaid, operatorName, deliveryTime"
              />
            </v-card-text>

            <v-card-text>
              <v-expansion-panels flat hover multiple>
                <v-expansion-panel>
                  <v-expansion-panel-header class="px-0 title">Financial settings</v-expansion-panel-header>
                  <v-expansion-panel-content>

                    <h3 class="mb-2 text--secondary">Z-Report</h3>

                    <time-input
                      label="Take Z-Report at"
                      v-model="ownOverview.zreport_time"
                      placeholder="03:00"
                      messages="Time in day that Z-Report will be created. Rounded to nearest full or half hour"
                      class="mb-4"
                    ></time-input>

                    <v-text-field
                      label="Verification series"
                      v-model="ownOverview.zreport_verification_series"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="Main cost center id"
                      v-model="ownOverview.main_cost_center_id"
                      messages="Id of cost center that is added to all verification rows"
                      autocomplete="off"
                      class="mb-4"
                    ></v-text-field>

                    <v-text-field
                      label="Main cost center name"
                      v-model="ownOverview.main_cost_center_name"
                      autocomplete="off"
                    ></v-text-field>

                    <h3 class="mt-8 mb-2 text--secondary">Financial year</h3>

                    <p class="mb-4 text--secondary">
                      Set start and end date of your financial year.
                      You can leave this empty if your financial year starts 1:st of January and ends 31:st of December
                    </p>

                    <date-input
                      label="Financial year start"
                      v-model="ownOverview.financial_year_start"
                      :placeholder="`${currentYear}-01-01`"
                      :persistent-placeholder="true"
                      clearable
                    ></date-input>

                    <date-input
                      label="Financial year end"
                      v-model="ownOverview.financial_year_end"
                      :placeholder="`${currentYear}-12-31`"
                      :persistent-placeholder="true"
                      clearable
                    ></date-input>

                    <div v-if="financialYearDetails.invalid" class="mb-12">
                      <v-alert
                        type="error"
                        outlined
                        dense
                      >
                        <div class="">
                          Invalid financial year. Start must be before end.
                        </div>
                      </v-alert>
                    </div>
                    <div v-else-if="financialYearDetails.odd" class="mb-12">
                      <v-alert
                        type="info"
                        outlined
                        dense
                      >
                        <div class="mb-1">
                          {{ financialYearDetails.extended ? 'Extended' : 'Shortened' }} financial year.
                        </div>
                        <div class="">
                          You will need to update this when the next financial year starts.
                        </div>
                      </v-alert>
                    </div>

                    <h3 class="mt-4 mb-2 text--secondary">Accounts per payment type</h3>

                    <p class="mb-4 text--secondary">Accounts to use for each payment type when exporting data to an SIE-file.</p>

                    <v-text-field
                      label="Swish"
                      :value="(ownOverview.payment_type_account_numbers || {}).SWISH"
                      @input="objectInput('payment_type_account_numbers', 'SWISH', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="App Card Payment"
                      :value="(ownOverview.payment_type_account_numbers || {}).CARDCHECKOUT"
                      @input="objectInput('payment_type_account_numbers', 'CARDCHECKOUT', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="IZettle"
                      :value="(ownOverview.payment_type_account_numbers || {}).IZETTLE"
                      @input="objectInput('payment_type_account_numbers', 'IZETTLE', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="Bambora"
                      :value="(ownOverview.payment_type_account_numbers || {}).BAMBORA"
                      @input="objectInput('payment_type_account_numbers', 'BAMBORA', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="Gift Cards"
                      :value="(ownOverview.payment_type_account_numbers || {}).GIFTCARD"
                      @input="objectInput('payment_type_account_numbers', 'GIFTCARD', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <h3 class="mt-4 mb-2 text--secondary">Accounts per VAT rate</h3>

                    <p class="mb-4 text--secondary">Accounts to use for each VAT rate when exporting data to an SIE-file.</p>

                    <v-text-field
                      label="25% VAT"
                      :value="(ownOverview.vat_account_numbers || {})['25']"
                      @input="objectInput('vat_account_numbers', '25', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="12% VAT"
                      :value="(ownOverview.vat_account_numbers || {})['12']"
                      @input="objectInput('vat_account_numbers', '12', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="6% VAT"
                      :value="(ownOverview.vat_account_numbers || {})['6']"
                      @input="objectInput('vat_account_numbers', '6', $event)"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <h3 class="mt-4 mb-2 text--secondary">Rounding Account</h3>

                    <p class="mb-4 text--secondary">Account to use for rounding in order for exports to balance.</p>

                    <v-text-field
                      label="Rounding Account"
                      v-model="ownOverview.rounding_account_number"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <h3 class="mt-4 mb-2 text--secondary">Invoice</h3>

                    <p class="mb-4 text--secondary">Account information for placement on invoices.</p>

                    <v-text-field
                      label="Plusgiro"
                      v-model="ownOverview.plusgiro_account_number"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="Bankgiro"
                      v-model="ownOverview.bankgiro_account_number"
                      type="tel"
                      autocomplete="off"
                    ></v-text-field>

                    <v-card>
                      <v-card-title>
                        Fortnox integration
                      </v-card-title>
                      <v-card-text>
                        <div v-if="ownOverview.fortnox_access_token">
                          <v-alert :type="ownOverview.fortnox_error ? 'error' : 'success'" dense outlined>
                            <div>
                              Fortnox integration is set up for account {{fortnoxData.sub}}
                            </div>
                            <div v-if="ownOverview.fortnox_client">
                              Uses "{{ownOverview.fortnox_client.title}}" ({{ownOverview.fortnox_client.client_id}})
                            </div>
                            <div v-if="ownOverview.fortnox_error">
                              But lastest integration attempt(s) has failed. May need to be set up anew
                            </div>
                          </v-alert>
                        </div>
                        <div v-else>

                        </div>
                        <div class="d-flex align-center">
                          <v-spacer />
                          <v-btn
                            v-if="ownOverview.fortnox_access_token"
                            @click="removeFortnox"
                            class="mr-2"
                            text
                            color="primary"
                          >Remove</v-btn>
                          <v-btn
                            color="primary"
                            @click="$confirm({
                              title: 'Set up Fortnox',
                              message: 'You will leave this page to set up Fortnox. Any unsaved changes will be lost. Continue?',
                              ok: 'Yes'
                            }).then(
                              startFornoxSetup,
                              () => {},
                            )"
                          >Set up</v-btn>
                        </div>
                      </v-card-text>
                    </v-card>

                    <v-dialog
                      v-model="fortnoxSetup"
                      max-width="400"
                      scrollable
                    >
                      <v-card>
                        <v-card-title>
                          Fortnox integration
                        </v-card-title>
                        <v-card-text>
                          <div v-if="!showFortnoxApps">
                            <div class="body-1 mb-6">
                              Do you have more than one FastTrack shop that connect with Fortnox?
                            </div>
                            <div class="d-flex align-center justify-center">
                              <v-btn color="primary" class="mr-4" @click="showFortnoxApps = true">Yes</v-btn>
                              <v-btn color="primary" @click="openFornoxSetup()">No</v-btn>
                            </div>
                          </div>

                          <div v-else>
                            <div class="body-1 mb-4">
                              Select one of the options below that you haven't used in a previous FastTrack shop.
                            </div>

                            <v-list>
                              <v-list-item
                                v-for="option in fortnoxApps"
                                :key="option.client_id"
                                two-line
                                @click="openFornoxSetup(option.client_id)"
                              >
                                <v-list-item-content>
                                  <v-list-item-title>
                                    {{option.title}}
                                  </v-list-item-title>
                                  <v-list-item-subtitle>
                                    Fortnox id: {{option.client_id}}
                                  </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                  <v-icon>mdi-chevron-right</v-icon>
                                </v-list-item-action>
                              </v-list-item>
                            </v-list>

                          </div>

                        </v-card-text>
                      </v-card>
                    </v-dialog>

                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-header class="px-0 title">Advanced</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <h3 class="mb-2 text--secondary">Online point of sale</h3>

                    <p class="mb-4 text--secondary">Settings for the online point of sale used for purchases made from the app.</p>

                    <v-text-field
                      label="Online point of sale Id"
                      v-model="ownOverview.online_point_of_sale_id"
                      placeholder="FTP001"
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="Online POS control unit Id"
                      v-model="ownOverview.online_pos_control_unit_id"
                      placeholder="RIHTT..."
                      autocomplete="off"
                    ></v-text-field>

                    <v-text-field
                      label="Online POS manufacturing number"
                      v-model="ownOverview.online_pos_manufacturer_number"
                      autocomplete="off"
                    ></v-text-field>

                    <v-checkbox
                      label="Remove 'payerAlias' (make anonymous)."
                      v-model="ownOverview.mask_payer_alias"
                    ></v-checkbox>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>

            <v-divider />

            <v-card-title>
              <v-btn depressed color="primary" type="submit" :disabled="!valid" :loading="isSaving">Save</v-btn>
            </v-card-title>
          </v-card>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {ImageInput} from 'dync-imageinput';
import {DateInput, TimeInput} from 'dync-datetime';
import {apiUrl} from '@dynapp/wc-api-generic';
import axios from 'axios';
import moment from 'moment-timezone';

export default {
  name: 'Overview',
  data () {
    return {
      ownOverview: {},
      imageName: 'dync-appshop-header.jpg',
      image: null,
      hadImage: false,
      valid: true,
      stringValidation: [v => !!v || 'Cannot be empty'],
      currentYear: (new Date()).getFullYear(),
      fortnoxSetup: false,
      showFortnoxApps: false,
      fortnoxApps: []
    }
  },
  watch: {
    overview (newVal) {
      this.setOwnOverview(newVal);
    }
  },
  computed: {
    isSaving () {
      return this.$store.getters.isLoadingFor(['overviews.update', 'overviews.create']);
    },
    overview () {
      const items = this.$store.getters['overviews/items'];
      return items.length > 0 ? items[0] : null;
    },
    fortnoxData () {
      if (
        this.ownOverview
        && this.ownOverview.fortnox_access_token
      ) {
        return JSON.parse(atob(this.ownOverview.fortnox_access_token.split('.')[1]));
      }
      return {};
    },
    financialYearDetails() {
      var start = moment(this.ownOverview.financial_year_start || `${this.currentYear}-01-01`);
      var end = moment(this.ownOverview.financial_year_end || `${this.currentYear}-12-31`);

      var details = {
        start, end
      };

      var expectedEnd = start.clone().add(1, 'year').subtract(1, 'day');
      if (expectedEnd.isSame(end)) {
        details.odd = false;
        details.extended = false;
        details.shortened = false;
      } else {
        details.odd = true;
        details.extended = end.isAfter(expectedEnd);
        details.shortened = !details.extended;
      }
      if (start.isSameOrAfter(end)) {
        details.invalid = true;
      }
      return details;
    }
  },
  methods: {
    submit () {
      const item = Object.assign({}, this.ownOverview);

      if (this.image instanceof Blob) {
        item.attachments = [{
          name: this.imageName,
          file: this.image
        }];
      }
      if (!this.image && this.hadImage) {
        item.attachments_remove = [this.imageName];
      }


      var promise;
      if (this.ownOverview.id) {
        promise = this.$store.dispatch('overviews/updateItem', item);
      } else {
        promise = this.$store.dispatch('overviews/createItem', item);
      }
      promise.then(() => {
        this.$snackbar({message: 'Overview saved!', theme: 'success'});
      }).catch((error) => {
        console.error(error);
        this.$snackbar({message: 'Could not save overview', theme: 'error'});
      })
    },
    setOwnOverview (overview) {
      this.image = null;
      this.ownOverview = Object.assign({}, overview);
      this.hadImage = false;
      if (this.ownOverview.attachment_names.indexOf(this.imageName) != -1) {
        this.hadImage = true;
        this.image = this.$store.getters['overviews/getAttachmentUrl'](overview.id, this.imageName);
      }
    },
    objectInput (key, objectKey, value) {
      var item = {...(this.ownOverview[key] || {})};
      item[objectKey] = value;
      this.$set(this.ownOverview, key, item);
    },
    startFornoxSetup() {
      this.fortnoxSetup = true;
      this.showFortnoxApps = false;
      axios.get(apiUrl({dataObject: 'dync-appshop-web'}, `fortnox/apps`)).then(
        (resp) => {
          this.fortnoxApps = resp.data;
        },
        () => {
          this.$confirm({
            title: 'Failed to get Fortnox options',
            message: 'Would you like to try again?'
          }).then(
            this.startFornoxSetup,
            () => {}
          );
        }
      );
    },
    openFornoxSetup(client_id) {
      if (!client_id) {
        location.href = apiUrl({dataObject: 'dync-appshop-web'}, 'fortnox/new-auth');
      } else {
        location.href = apiUrl({dataObject: 'dync-appshop-web'}, 'fortnox/new-auth/' + client_id);
      }
    },
    removeFortnox() {
      this.$set(this.ownOverview, 'fortnox_access_token', null);
      this.$set(this.ownOverview, 'fortnox_refresh_token', null);
      this.$set(this.ownOverview, 'fortnox_error', null);
      this.$set(this.ownOverview, 'fortnox_client', null);
      this.$dialog({
        title: 'Notice',
        message: 'Save overview to complete removal'
      });
    }
  },
  mounted () {
    this.$store.dispatch('overviews/fetchItems');
    if (this.overview) {
      this.setOwnOverview(this.overview);
    }
    if (this.$route.query.fortnox_success) {
      if (this.$route.query.fortnox_success == 'true') {
        this.$snackbar({message: 'Fortnox integration successful!', theme: 'success'});
      } else {
        if (this.$route.query.fortnox_error) {
          this.$dialog({
            title: 'Fortnox integration failed',
            message: `Could not set up Fortnox integration. Fortnox responded with:\n\n${this.$route.query.fortnox_error_desc || '-'}\n\n(${this.$route.query.fortnox_error})`
          });
        } else {
          this.$snackbar({message: 'Fortnox integration failed', theme: 'error'});
        }
      }
      this.$router.replace(this.$route.path);
    }
  },
  components: {
    ImageInput,
    DateInput,
    TimeInput
  }
}
</script>

<style lang="scss" scoped>
  .overview__image-uploader {
    height: 500px;
  }
</style>
